const TAB_GAP_PX = 4;
const OVERFLOW_BUTTON_PX = 100;
const END_SLOT_RESERVE_PX = 140;

/**
 * Slack kept inside the patient tab row so the last label cannot meet the
 * edge. «Tilpas faner» sits in the flex gap after this row, not inside it.
 */
export const TAB_BAR_FIT_SAFETY_PX = 8;

export function countVisibleTabs(input: {
  itemCount: number;
  itemWidths: readonly number[];
  rowWidth: number;
  hasEndSlot: boolean;
  activeIndex: number;
}): number {
  if (input.itemCount <= 0) return 0;
  const toolsWidth = input.hasEndSlot ? END_SLOT_RESERVE_PX : 0;
  const available = input.rowWidth - toolsWidth;
  if (available <= 0) return input.itemCount;

  const fit = (maxWidth: number): number => {
    let used = 0;
    for (let i = 0; i < input.itemWidths.length; i += 1) {
      used += input.itemWidths[i] + TAB_GAP_PX;
      if (used > maxWidth) return Math.max(1, i);
    }
    return input.itemCount;
  };

  let count = fit(available);
  if (count < input.itemCount) {
    count = fit(available - OVERFLOW_BUTTON_PX);
  }
  if (input.activeIndex >= count) {
    count = Math.min(input.itemCount, input.activeIndex + 1);
  }
  return count;
}

export interface TabBarLayout {
  /** Indexes on the bar, left to right. */
  visible: number[];
  /** Indexes in the overflow menu, in the original tab order. */
  overflow: number[];
}

/**
 * Tabs that fit in the patient tab row.
 *
 * `rowWidth` is only the tab strip. The customize control is a sibling after
 * that strip, so a tab that does not fit has to move into the overflow menu.
 * Stretching the visible prefix out to a selected last tab (Rapporter) paints
 * that label on top of «Tilpas faner». The selected tab stays on the bar when
 * its own width fits; tabs that do not fit beside it move into the menu.
 */
export function layoutTabBar(input: {
  itemWidths: readonly number[];
  rowWidth: number;
  activeIndex: number;
  /** Width of the overflow control. Ignored when every tab fits. */
  overflowWidth: number;
}): TabBarLayout {
  const n = input.itemWidths.length;
  const all = Array.from({ length: n }, (_, index) => index);
  if (n === 0) return { visible: [], overflow: [] };
  // A hidden strip (display:none) reports 0. Keep every tab mounted.
  if (input.rowWidth <= 0) return { visible: all, overflow: [] };

  const widths = input.itemWidths;
  const budget = Math.max(0, input.rowWidth - TAB_BAR_FIT_SAFETY_PX);
  const sum = (indices: readonly number[]) =>
    indices.reduce((total, index) => total + widths[index], 0);

  if (sum(all) <= budget) return { visible: all, overflow: [] };

  const overflowWidth = Math.max(0, input.overflowWidth);
  const active = input.activeIndex >= 0 && input.activeIndex < n ? input.activeIndex : 0;
  const visible: number[] = [];
  const hidden: number[] = [];
  let used = widths[active] + overflowWidth;
  let blocked = used > budget;

  for (let index = 0; index < n; index += 1) {
    if (index === active) {
      visible.push(index);
      continue;
    }
    if (blocked) {
      hidden.push(index);
      continue;
    }
    const next = used + widths[index];
    if (next <= budget) {
      used = next;
      visible.push(index);
    } else {
      blocked = true;
      hidden.push(index);
    }
  }

  return { visible, overflow: hidden };
}
